import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { RefreshCw, Download, HandCoins, ChevronDown, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import api from '../../utils/api';

const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const STATUS = {
  pending: { label: 'Da applicare', cls: 'bg-amber-100 text-amber-800' },
  partially_applied: { label: 'Parziale', cls: 'bg-sky-100 text-sky-800' },
  applied: { label: 'Incassata ✓', cls: 'bg-emerald-100 text-emerald-800' },
  exempt: { label: 'Esente', cls: 'bg-slate-200 text-slate-600' },
  waiting_booking: { label: 'In attesa prenotazione', cls: 'bg-rose-100 text-rose-700' },
};
const TYPE_LBL = { non_refundable: 'Non rimb.', refundable: 'Rimb.' };

const downloadB64 = (name, b64) => {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const smartMatch = (it, q) => {
  const hay = norm([it.property_code, it.property_name, it.city, it.owner_name,
    STATUS[it.status]?.label, it.allocation_source, it.exempt_reason, it.override_note, it.last_check_in,
    ...(it.allocations || []).map(a => `${a.booking_code} ${a.check_in}`)].join(' '));
  return norm(q).split(/\s+/).filter(Boolean).every(t => hay.includes(t));
};

const SORT_ACCESSORS = {
  property: it => norm(`${it.property_code} ${it.property_name}`),
  owner: it => norm(it.owner_name),
  status: it => STATUS[it.status]?.label || it.status || '',
  target: it => it.allocations?.[0]?.check_in || '9999-99-99',
  amount: it => it.applied_total || 0,
};

function SortableTh({ label, col, sort, setSort, align = 'left' }) {
  const active = sort.col === col;
  const Icon = !active ? ArrowUpDown : sort.dir === 1 ? ArrowUp : ArrowDown;
  return (
    <th className={`p-2 text-${align} select-none cursor-pointer hover:text-slate-800 whitespace-nowrap`}
      data-testid={`fee-sort-${col}`}
      onClick={() => setSort(s => s.col === col ? { col, dir: -s.dir } : { col, dir: 1 })}>
      <span className={`inline-flex items-center gap-1 ${active ? 'text-slate-900 font-semibold' : ''}`}>
        {label} <Icon className={`h-3 w-3 ${active ? 'text-emerald-600' : 'text-slate-300'}`} />
      </span>
    </th>
  );
}

function FeeTable({ title, subtitle, items, testid, emptyText, override, markApplied, accent }) {
  const [sort, setSort] = useState({ col: 'property', dir: 1 });
  const [open, setOpen] = useState({});
  const [collapsed, setCollapsed] = useState(false);

  const sorted = [...items].sort((a, b) => {
    const va = SORT_ACCESSORS[sort.col](a); const vb = SORT_ACCESSORS[sort.col](b);
    return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
  });

  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setCollapsed(c => !c)}>
        <div className="flex items-center gap-2">
          {collapsed ? <ChevronRight className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
          <CardTitle className={`text-base ${accent || ''}`}>{title}</CardTitle>
          <span className="text-xs text-slate-400">{subtitle}</span>
          <span className="ml-auto text-sm text-slate-500" data-testid={`${testid}-count`}>{items.length} immobili</span>
        </div>
      </CardHeader>
      {!collapsed && <CardContent>
        {!sorted.length ? <p className="text-sm text-slate-400 py-4 text-center">{emptyText || 'Nessun immobile'}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs text-slate-500 border-b">
                <th className="w-6"></th>
                <SortableTh label="Immobile" col="property" sort={sort} setSort={setSort} />
                <SortableTh label="Proprietario" col="owner" sort={sort} setSort={setSort} />
                <SortableTh label="Stato" col="status" sort={sort} setSort={setSort} />
                <SortableTh label="Trattenere da" col="target" sort={sort} setSort={setSort} />
                <SortableTh label="Trattenuto" col="amount" sort={sort} setSort={setSort} align="right" />
                <th className="text-left p-2 pl-4">Azioni</th>
              </tr></thead>
              <tbody className="divide-y">
                {sorted.map(it => {
                  const isOpen = open[it.id];
                  const first = (it.allocations || [])[0];
                  return (
                    <React.Fragment key={it.id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" data-testid={`fee-row-${it.property_code}`} onClick={() => setOpen(o => ({ ...o, [it.id]: !o[it.id] }))}>
                        <td className="p-2 text-slate-400">{(it.allocations || []).length ? (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : ''}</td>
                        <td className="p-2"><span className="font-mono text-xs">{it.property_code}</span> <span className="font-medium">{it.property_name}</span><span className="text-slate-400 text-xs"> · {it.city}</span></td>
                        <td className="p-2 text-xs">{it.owner_name || '—'}</td>
                        <td className="p-2">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS[it.status]?.cls || ''}`}>{STATUS[it.status]?.label || it.status}</span>
                          {it.exempt_reason && <p className="text-[10px] text-slate-400 mt-0.5">{it.exempt_reason}</p>}
                          {it.uncovered > 0 && it.status !== 'exempt' && <p className="text-[10px] text-rose-500 mt-0.5">scoperto {eur(it.uncovered)}</p>}
                        </td>
                        <td className="p-2 text-xs">
                          {first ? <>{(it.allocations.length > 1) && <b>{it.allocations.length} quote · </b>}<span className="font-mono">{first.booking_code}</span> ({first.check_in}{first.booking_type ? `, ${TYPE_LBL[first.booking_type] || first.booking_type}` : ''})</> :
                            it.status === 'exempt' ? '—' : <span className="text-rose-500">nessuna prenotazione futura utile{it.last_check_in ? <span className="text-slate-400 font-normal"> · ultimo check-in <b>{it.last_check_in}</b></span> : <span className="text-slate-400 font-normal"> · mai avuto prenotazioni</span>}</span>}
                          {it.allocation_source && <span className="text-slate-400"> · {it.allocation_source}</span>}
                        </td>
                        <td className="p-2 text-right text-xs font-medium">{it.status === 'exempt' ? '—' : `${eur(it.applied_total || 0)} / ${eur(it.amount)}`}</td>
                        <td className="p-2 pl-4" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1 flex-wrap">
                            {it.override !== 'force_pay' && it.status === 'exempt' && <button className="text-[10px] px-1.5 py-0.5 border rounded text-slate-500 hover:bg-slate-100" title="Uscita anticipata concessa: paga comunque" data-testid={`fee-force-${it.property_code}`} onClick={() => override(it, 'force_pay')}>Paga comunque</button>}
                            {it.override !== 'exempt' && it.status !== 'exempt' && <button className="text-[10px] px-1.5 py-0.5 border rounded text-slate-500 hover:bg-slate-100" data-testid={`fee-exempt-${it.property_code}`} onClick={() => override(it, 'exempt')}>Esenta</button>}
                            {it.override && <button className="text-[10px] px-1.5 py-0.5 border rounded text-amber-600 hover:bg-amber-50" data-testid={`fee-clear-${it.property_code}`} onClick={() => override(it, 'clear')}>↩ Regola auto</button>}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (it.allocations || []).map(a => (
                        <tr key={a.booking_code} className="bg-slate-50/60" data-testid={`fee-alloc-${a.booking_code}`}>
                          <td></td>
                          <td className="p-2 pl-8 text-xs" colSpan={2}>
                            <span className="font-mono">{a.booking_code}</span> · check-in {a.check_in}{a.booking_type ? ` · ${TYPE_LBL[a.booking_type] || a.booking_type}` : ''} · compenso {eur(a.owner_amount)}
                          </td>
                          <td className="p-2 text-xs">
                            {a.applied ? <span className="text-emerald-700 font-semibold">✓ trattenuta {a.applied_source === 'hw_sync' ? '(rilevata da HW)' : a.applied_source === 'payment_check' ? '(rilevata nel controllo pagamenti)' : '(manuale)'}</span> : <span className="text-amber-600">da trattenere</span>}
                            {a.applied_ref && <p className="text-[10px] text-slate-400">{a.applied_ref}</p>}
                          </td>
                          <td className="p-2 text-xs font-bold">{eur(a.amount)}</td>
                          <td className="p-2 text-right text-xs"></td>
                          <td className="p-2 pl-4">
                            <button className={`text-[10px] px-1.5 py-0.5 border rounded ${a.applied ? 'text-slate-400 hover:bg-slate-100' : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'}`}
                              data-testid={`fee-mark-${a.booking_code}`} onClick={() => markApplied(it, a, !a.applied)}>
                              {a.applied ? 'Riporta da applicare' : '✓ Segna trattenuta'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>}
    </Card>
  );
}

export default function ServiceFeePage() {
  const defaultYear = () => {
    const now = new Date();
    return now.getMonth() + 1 >= 10 ? now.getFullYear() + 1 : (now.getMonth() + 1 <= 3 ? now.getFullYear() : now.getFullYear() + 1);
  };
  const [year, setYear] = useState(defaultYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/service-fees?year=${year}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore caricamento'); }
    finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    setComputing(true);
    try {
      const r = await api.post(`/service-fees/compute?year=${year}`);
      toast.success(`Piano ${r.data.year} aggiornato — ${r.data.stats.total} immobili${r.data.detected_applied ? ` · ${r.data.detected_applied} quote rilevate come già trattenute` : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore calcolo'); }
    finally { setComputing(false); }
  };

  const override = async (item, action) => {
    const labels = { force_pay: 'PAGA COMUNQUE (uscita anticipata concessa)', exempt: 'ESENTE', clear: 'regola automatica' };
    if (!window.confirm(`${item.property_code} · ${item.property_name}: impostare "${labels[action]}"?`)) return;
    try {
      await api.post(`/service-fees/${item.id}/override`, { action });
      toast.success('Impostazione salvata, piano ricalcolato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const markApplied = async (item, alloc, applied) => {
    try {
      await api.post(`/service-fees/${item.id}/mark-applied`, { booking_code: alloc.booking_code, applied });
      toast.success(applied ? 'Quota segnata come trattenuta' : 'Quota riportata da applicare');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get(`/service-fees/export?year=${year}`);
      downloadB64(r.data.filename, r.data.b64);
    } catch (e) { toast.error('Errore export'); }
  };

  const s = data?.summary;
  const all = (data?.items || []).filter(i => !search || smartMatch(i, search));
  const toCollect = all.filter(i => ['pending', 'partially_applied', 'waiting_booking'].includes(i.status));
  const collected = all.filter(i => i.status === 'applied');
  const exempt = all.filter(i => i.status === 'exempt');
  const tableProps = { override, markApplied };
  const TABS = [
    { key: 'pending', label: 'Da incassare', items: toCollect, cls: 'data-[active=true]:bg-amber-500' },
    { key: 'collected', label: 'Incassate', items: collected, cls: 'data-[active=true]:bg-emerald-600' },
    { key: 'exempt', label: 'Esenti', items: exempt, cls: 'data-[active=true]:bg-slate-500' },
  ];

  return (
    <div className="space-y-5 p-1" data-testid="service-fee-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><HandCoins className="h-6 w-6 text-emerald-600" /> Canone Annuale — Costi Servizio</h1>
          <p className="text-sm text-slate-500">120 €/immobile per l'anno {year}, trattenuti da prenotazioni FUTURE (Q4 {year - 1} in priorità). Le prenotazioni target escono in automatico nel Controllo Pagamenti.</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="border rounded-md h-9 px-2 text-sm" value={year} onChange={e => setYear(Number(e.target.value))} data-testid="fee-year-select">
            {[0, 1, 2].map(d => { const y = new Date().getFullYear() + d; return <option key={y} value={y}>{y}</option>; })}
          </select>
          <Button variant="outline" size="sm" onClick={exportXlsx} data-testid="fee-export-btn"><Download className="h-4 w-4 mr-1" /> Excel</Button>
          <Button size="sm" onClick={compute} disabled={computing} data-testid="fee-compute-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${computing ? 'animate-spin' : ''}`} /> {data?.items?.length ? 'Aggiorna piano' : 'Calcola piano'}
          </Button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[['Immobili', s.total, ''], ['Da applicare', s.pending, 'text-amber-600'], ['Incassate', s.applied, 'text-emerald-600'],
            ['Parziali', s.partially_applied, 'text-sky-600'], ['Esenti', s.exempt, 'text-slate-500'], ['In attesa pren.', s.waiting_booking, 'text-rose-600']].map(([l, v, cls]) => (
            <Card key={l}><CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${cls}`} data-testid={`fee-stat-${l}`}>{v ?? 0}</p>
              <p className="text-xs text-slate-500">{l}</p>
            </CardContent></Card>
          ))}
        </div>
      )}
      {s && (
        <p className="text-sm text-slate-600" data-testid="fee-amount-summary">
          Totale da incassare: <b>{eur(s.amount_total)}</b> · già trattenuto: <b className="text-emerald-700">{eur(s.amount_applied)}</b>
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Filtro intelligente: casa, codice, proprietario, prenotazione, città, stato…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-md h-9" data-testid="fee-search" />
        <div className="flex rounded-lg border overflow-hidden ml-auto">
          {TABS.map(t => (
            <button key={t.key} data-active={tab === t.key} data-testid={`fee-tab-${t.key}`}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${tab === t.key ? (t.key === 'pending' ? 'bg-amber-500 text-white' : t.key === 'collected' ? 'bg-emerald-600 text-white' : 'bg-slate-500 text-white') : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              onClick={() => setTab(t.key)}>
              {t.label} <span className={`ml-1 text-xs ${tab === t.key ? 'opacity-80' : 'text-slate-400'}`}>({t.items.length})</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-400 py-6 text-center">Caricamento…</p> :
        !data?.items?.length ? <p className="text-sm text-slate-400 py-6 text-center" data-testid="fee-empty">Nessun dato — premi "Calcola piano"</p> : (
          <>
            {tab === 'pending' && <FeeTable title="Da incassare" subtitle="da applicare · parziali · in attesa di prenotazione" accent="text-amber-700"
              items={toCollect} testid="fee-table-pending" emptyText="Nessun canone da trattenere 🎉" {...tableProps} />}
            {tab === 'collected' && <FeeTable title="Incassate" subtitle="canoni già trattenuti (HW, controllo pagamenti o manuali)" accent="text-emerald-700"
              items={collected} testid="fee-table-collected" emptyText="Nessun canone ancora incassato" {...tableProps} />}
            {tab === 'exempt' && <FeeTable title="Esenti" subtitle="case che cessano entro l'anno o esentate manualmente" accent="text-slate-500"
              items={exempt} testid="fee-table-exempt" emptyText="Nessun immobile esente" {...tableProps} />}
          </>
        )}
    </div>
  );
}
